import { useEffect, useState } from 'react';
import type { CanvasSummary, VideoReference } from '@/api/types';
import { Dialog } from '@/app/layers/Dialog';
import { cx } from '@/lib/cx';
import { useT } from '@/i18n/LocaleProvider';
import styles from './Overlays.module.css';

interface Props {
  video: VideoReference | null;
  saved: boolean;
  canvases: CanvasSummary[];
  onClose: () => void;
  onToggleSave: (video: VideoReference) => void;
  onAddToCanvas: (video: VideoReference, canvasId: number, alreadyPresent: boolean) => void;
  onNewCanvas: (video: VideoReference) => void;
  onOpen: (video: VideoReference) => void;
}

/** Мобильный action sheet после long-press: Save / Add to canvas / Open breakdown (README «Mobile»). */
export function CardActionSheet({
  video,
  saved,
  canvases,
  onClose,
  onToggleSave,
  onAddToCanvas,
  onNewCanvas,
  onOpen,
}: Props) {
  const [step, setStep] = useState<'main' | 'canvas'>('main');
  useEffect(() => {
    if (video) setStep('main');
  }, [video]);

  const open = !!video;
  const t = useT();
  return (
    <Dialog
      open={open}
      onClose={onClose}
      ariaLabel={video?.title ?? t('catalog.cardActions')}
      sheetOnMobile
      className={styles.sheetPanel}
      maxWidth={420}
    >
      {video ? (
        <>
          <div className={styles.sheetHead}>
            <img src={video.extra?.thumbnail_url ?? ''} alt="" className={styles.sheetThumb} />
            <div className={styles.sheetTitleWrap}>
              <div className={styles.sheetTitle}>{video.title}</div>
              <div className={styles.sheetMeta}>
                {t('catalog.levelMeta', {
                  pacing: video.pacing ?? '—',
                  level: video.production_level ?? '—',
                })}
              </div>
            </div>
            <button
              type="button"
              className={styles.sheetClose}
              aria-label={t('common.close')}
              onClick={onClose}
            >
              ×
            </button>
          </div>

          {step === 'main' ? (
            <div className={styles.sheetActions}>
              <button
                type="button"
                className={cx(styles.sheetSave, saved && styles.sheetSaveOn)}
                onClick={() => onToggleSave(video)}
              >
                <span className={styles.sheetSaveIcon} aria-hidden="true" />
                {saved ? t('catalog.savedRemove') : t('catalog.save')}
              </button>
              <button type="button" className={styles.sheetGhost} onClick={() => setStep('canvas')}>
                <span className={styles.sheetGhostIcon} aria-hidden="true" />
                {t('catalog.addToCanvas')}
              </button>
              <button type="button" className={styles.sheetQuiet} onClick={() => onOpen(video)}>
                {t('catalog.openBreakdown')}
              </button>
            </div>
          ) : (
            <>
              <p className={styles.sheetEyebrow}>{t('catalog.addToCanvas')}</p>
              <div className={styles.sheetList}>
                {canvases.map((canvas) => {
                  const on = video.saved_canvas_ids.includes(canvas.id);
                  return (
                    <button
                      key={canvas.id}
                      type="button"
                      className={styles.sheetCanvas}
                      onClick={() => onAddToCanvas(video, canvas.id, on)}
                    >
                      {canvas.cover_url ? (
                        <img src={canvas.cover_url} alt="" className={styles.sheetCanvasCover} />
                      ) : (
                        <span className={styles.sheetCanvasCover} />
                      )}
                      <span className={styles.sheetCanvasName} data-clarity-mask="true">
                        {canvas.name}
                      </span>
                      <span
                        className={cx(styles.sheetCanvasState, on && styles.sheetCanvasStateOn)}
                      >
                        {on ? t('picker.added') : t('picker.add')}
                      </span>
                    </button>
                  );
                })}
                <button
                  type="button"
                  className={styles.sheetNew}
                  onClick={() => onNewCanvas(video)}
                >
                  {t('catalog.newCanvas')}
                </button>
              </div>
            </>
          )}
        </>
      ) : null}
    </Dialog>
  );
}
